import { bit, type Difficulty, type Grid } from '../sudoku/types';
import { PEERS } from '../sudoku/units';

/**
 * Spielzustand einer Partie inklusive Rückgängig-/Wiederherstellen-Verlauf.
 * Die Klasse ist bewusst frei von DOM-Zugriffen, damit sie testbar bleibt.
 */

export interface Move {
  index: number;
  prevValue: number;
  prevNotes: number;
  nextValue: number;
  nextNotes: number;
  /**
   * Züge mit derselben Gruppe gehören zusammen und werden gemeinsam
   * zurückgenommen (z. B. eine Ziffer plus die dadurch gelöschten Notizen).
   */
  group: number;
}

export interface GameSnapshot {
  puzzle: number[];
  solution: number[];
  values: number[];
  notes: number[];
  difficulty: Difficulty;
  elapsedMs: number;
  mistakes: number;
  hintsUsed: number;
  seed: number;
  completed: boolean;
}

export class Game {
  readonly puzzle: Grid;
  readonly solution: Grid;
  readonly difficulty: Difficulty;
  readonly seed: number;

  values: Uint8Array;
  notes: Uint16Array;
  elapsedMs = 0;
  mistakes = 0;
  hintsUsed = 0;
  completed = false;

  private undoStack: Move[] = [];
  private redoStack: Move[] = [];
  private groupCounter = 0;

  constructor(puzzle: Grid, solution: Grid, difficulty: Difficulty, seed: number) {
    this.puzzle = Uint8Array.from(puzzle);
    this.solution = Uint8Array.from(solution);
    this.difficulty = difficulty;
    this.seed = seed;
    this.values = Uint8Array.from(puzzle);
    this.notes = new Uint16Array(81);
  }

  isGiven(index: number): boolean {
    return this.puzzle[index] !== 0;
  }

  isCorrect(index: number): boolean {
    return this.values[index] === this.solution[index];
  }

  /** Falsch gesetzte Ziffer (nicht leer und nicht Teil der Lösung). */
  isWrong(index: number): boolean {
    return this.values[index] !== 0 && this.values[index] !== this.solution[index];
  }

  get filledCount(): number {
    let n = 0;
    for (let i = 0; i < 81; i++) if (this.values[i] !== 0) n++;
    return n;
  }

  /** Wie oft eine Ziffer bereits korrekt gesetzt wurde (für die Tastatur). */
  countDigit(digit: number): number {
    let n = 0;
    for (let i = 0; i < 81; i++) if (this.values[i] === digit) n++;
    return n;
  }

  isSolved(): boolean {
    for (let i = 0; i < 81; i++) if (this.values[i] !== this.solution[i]) return false;
    return true;
  }

  // -- Züge ----------------------------------------------------------------

  private commit(index: number, nextValue: number, nextNotes: number, group: number): Move | null {
    const prevValue = this.values[index];
    const prevNotes = this.notes[index];
    if (prevValue === nextValue && prevNotes === nextNotes) return null;

    const move: Move = { index, prevValue, prevNotes, nextValue, nextNotes, group };
    this.values[index] = nextValue;
    this.notes[index] = nextNotes;
    this.undoStack.push(move);
    this.redoStack.length = 0;
    return move;
  }

  /**
   * Setzt eine Ziffer. Gleiche Ziffer erneut = löschen.
   * Liefert `wrong`, wenn die Eingabe nicht zur Lösung passt.
   */
  setValue(index: number, digit: number): { changed: boolean; wrong: boolean } {
    if (this.isGiven(index) || this.completed) return { changed: false, wrong: false };

    const group = ++this.groupCounter;
    const next = this.values[index] === digit ? 0 : digit;
    const move = this.commit(index, next, 0, group);
    if (!move) return { changed: false, wrong: false };

    const wrong = next !== 0 && next !== this.solution[index];
    if (wrong) this.mistakes++;
    if (next !== 0 && !wrong) this.clearNotesOfPeers(index, next, group);
    return { changed: true, wrong };
  }

  /** Entfernt die Notiz `digit` bei allen Nachbarzellen – spart Handarbeit. */
  private clearNotesOfPeers(index: number, digit: number, group: number): void {
    const b = bit(digit);
    for (const p of PEERS[index]) {
      if (this.notes[p] & b) {
        const prevNotes = this.notes[p];
        this.notes[p] = prevNotes & ~b;
        // Teil desselben Zuges: gleiche Gruppe wie das Setzen der Ziffer
        this.undoStack.push({
          index: p,
          prevValue: this.values[p],
          prevNotes,
          nextValue: this.values[p],
          nextNotes: this.notes[p],
          group,
        });
      }
    }
  }

  toggleNote(index: number, digit: number): boolean {
    if (this.isGiven(index) || this.completed || this.values[index] !== 0) return false;
    const nextNotes = this.notes[index] ^ bit(digit);
    return this.commit(index, 0, nextNotes, ++this.groupCounter) !== null;
  }

  clear(index: number): boolean {
    if (this.isGiven(index) || this.completed) return false;
    return this.commit(index, 0, 0, ++this.groupCounter) !== null;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Nimmt den letzten Zug zurück; liefert die betroffenen Felder. */
  undo(): number[] {
    const last = this.undoStack.at(-1);
    if (!last) return [];
    const touched: number[] = [];
    while (this.undoStack.length && this.undoStack.at(-1)!.group === last.group) {
      const move = this.undoStack.pop()!;
      this.values[move.index] = move.prevValue;
      this.notes[move.index] = move.prevNotes;
      this.redoStack.push(move);
      touched.push(move.index);
    }
    return touched;
  }

  /** Stellt den zuletzt zurückgenommenen Zug wieder her. */
  redo(): number[] {
    const last = this.redoStack.at(-1);
    if (!last) return [];
    const touched: number[] = [];
    while (this.redoStack.length && this.redoStack.at(-1)!.group === last.group) {
      const move = this.redoStack.pop()!;
      this.values[move.index] = move.nextValue;
      this.notes[move.index] = move.nextNotes;
      this.undoStack.push(move);
      touched.push(move.index);
    }
    return touched;
  }

  /** Füllt alle Notizen anhand der aktuellen Belegung (Kandidaten-Autofill). */
  fillNotes(): void {
    const group = ++this.groupCounter;
    for (let i = 0; i < 81; i++) {
      if (this.values[i] !== 0) continue;
      let mask = 0;
      for (let d = 1; d <= 9; d++) {
        let possible = true;
        for (const p of PEERS[i]) {
          if (this.values[p] === d) {
            possible = false;
            break;
          }
        }
        if (possible) mask |= bit(d);
      }
      const prevNotes = this.notes[i];
      if (prevNotes === mask) continue;
      this.notes[i] = mask;
      this.undoStack.push({
        index: i,
        prevValue: 0,
        prevNotes,
        nextValue: 0,
        nextNotes: mask,
        group,
      });
    }
    this.redoStack.length = 0;
  }

  // -- Speichern -----------------------------------------------------------

  toSnapshot(): GameSnapshot {
    return {
      puzzle: Array.from(this.puzzle),
      solution: Array.from(this.solution),
      values: Array.from(this.values),
      notes: Array.from(this.notes),
      difficulty: this.difficulty,
      elapsedMs: this.elapsedMs,
      mistakes: this.mistakes,
      hintsUsed: this.hintsUsed,
      seed: this.seed,
      completed: this.completed,
    };
  }

  static fromSnapshot(snapshot: GameSnapshot): Game {
    const game = new Game(
      Uint8Array.from(snapshot.puzzle),
      Uint8Array.from(snapshot.solution),
      snapshot.difficulty,
      snapshot.seed ?? 0,
    );
    game.values = Uint8Array.from(snapshot.values);
    game.notes = Uint16Array.from(snapshot.notes);
    game.elapsedMs = snapshot.elapsedMs ?? 0;
    game.mistakes = snapshot.mistakes ?? 0;
    game.hintsUsed = snapshot.hintsUsed ?? 0;
    game.completed = snapshot.completed ?? false;
    return game;
  }
}
